import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Text } from '../ui';
import { kc } from '../auth/keycloak';
import { acknowledgeSessionExpiry, onSessionExpired } from '../auth/sessionExpiry';
import { initRedirectUri, rememberReturnPath } from '../auth/returnPath';

/**
 * Shown when the SSO session has ended.
 *
 * It can be dismissed, which is a deliberate choice rather than an oversight:
 * someone mid-way through a form may want to read what is on screen, or copy
 * something out of it, before signing in again. Dismissing only hides the
 * message — every request behind it still fails — so the acknowledgement clears
 * the channel's latch and the next failing request raises this again.
 */
export default function SessionExpiredDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => onSessionExpired(() => setOpen(true)), []);

  const dismiss = () => {
    setOpen(false);
    acknowledgeSessionExpiry();
  };

  const signIn = () => {
    // Stored before the redirect so the person comes back to the page they were
    // on rather than the root. See auth/returnPath.ts.
    rememberReturnPath(window.location, window.sessionStorage);
    void kc.login({ redirectUri: initRedirectUri(window.location) });
  };

  return (
    <Dialog open={open} onClose={dismiss}>
      <DialogTitle>{t('auth.sessionExpired.title')}</DialogTitle>
      <DialogContent>
        <Text>{t('auth.sessionExpired.message')}</Text>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} tone="neutral" emphasis="plain">
          {t('auth.sessionExpired.dismiss')}
        </Button>
        <Button onClick={signIn} emphasis="solid">
          {t('auth.sessionExpired.signIn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
